<template>
  <transition name="orbit-create" appear>
  <div v-if="visible" class="orbit-create" role="presentation">
    <section
      class="orbit-create__panel"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <div class="orbit-create__handle" />
      <header class="orbit-create__header">
        <div>
          <h2>{{ title }}</h2>
          <p>{{ $t('全自动 AI 交易，适合所有行情') }}</p>
        </div>
        <button type="button" :aria-label="$t('关闭')" :disabled="submitting" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <div class="orbit-create__summary">
        <span class="orbit-create__icon"><i class="el-icon-refresh" /></span>
        <div>
          <strong>UPlus-Orbit AI</strong>
          <small>{{ $t('AI 网格') }} · {{ $t('合约') }}</small>
        </div>
        <em>{{ $t('首推') }}</em>
      </div>

      <div class="orbit-create__field">
        <label>{{ $t('交易所') }}</label>
        <div class="orbit-create__readonly">
          <i class="orbit-create__dot" />
          <strong>{{ exchange || 'OKX' }}</strong>
        </div>
      </div>

      <div class="orbit-create__field">
        <div class="orbit-create__label-row">
          <label>{{ $t('投入金额 (USDT)') }}</label>
          <button type="button" :disabled="balanceLoading || submitting" @click="loadBalance">
            {{ balanceLoading ? $t('加载中…') : $t('刷新') }}
          </button>
        </div>
        <div class="orbit-create__input">
          <input
            v-model.trim="amount"
            inputmode="decimal"
            :placeholder="$t('请输入投入金额')"
            :disabled="submitting"
          >
          <span>USDT</span>
        </div>
        <div class="orbit-create__balance">
          <span>{{ $t('可用余额') }}</span>
          <strong>{{ balanceText }} USDT</strong>
        </div>
        <small class="orbit-create__hint">{{ $t('最低 1,500 USDT，最高 10,000 USDT') }}</small>
        <small v-if="amountExceedsBalance" class="orbit-create__error">{{ $t('投入金额不能超过交易所可用余额') }}</small>
      </div>

      <div class="orbit-create__field">
        <label>{{ $t('固定杠杆') }}</label>
        <div class="orbit-create__readonly orbit-create__readonly--between">
          <span>{{ $t('Orbit 固定使用 10x 杠杆') }}</span>
          <strong>{{ leverage }}x</strong>
        </div>
      </div>

      <div class="orbit-create__notice">
        <i class="el-icon-success" />
        <span>{{ $t('全自动 AI 交易，适合所有行情') }}</span>
      </div>

      <footer class="orbit-create__footer">
        <button type="button" :disabled="submitting" @click="close">{{ $t('取消') }}</button>
        <button type="button" :disabled="submitting || balanceLoading" @click="submit">
          {{ submitting ? $t('处理中…') : (mode === 'modify' ? $t('确认修改') : $t('确认启动')) }}
        </button>
      </footer>
    </section>
  </div>
  </transition>
</template>

<script>
import { loadStrategyBalance } from '@/api/strategy'
import { ORBIT_I18N_KEYS, ORBIT_LIMITS, orbitAmountIssue } from '@/api/orbit-strategy'

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

export default {
  name: 'OrbitStrategyCreateModal',
  props: {
    visible: { type: Boolean, default: false },
    exchange: { type: String, default: 'OKX' },
    robotInfo: { type: Object, default: null },
    mode: { type: String, default: 'create' },
    robot: { type: Object, default: () => ({ raw: {} }) },
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      amount: '',
      balance: null,
      balanceLoading: false,
      balanceLoaded: false,
      balanceRequest: 0
    }
  },
  computed: {
    title() {
      return this.$t(this.mode === 'modify' ? '修改 UPlus-Orbit AI' : '启动 UPlus-Orbit AI')
    },
    leverage() {
      return this.mode === 'modify' && this.robot.raw && this.robot.raw.leverage || ORBIT_LIMITS.leverage
    },
    amountExceedsBalance() {
      return this.mode !== 'modify' && this.balanceLoaded && finiteNumber(this.amount) !== null &&
        finiteNumber(this.amount) > finiteNumber(this.balance)
    },
    balanceText() {
      if (this.balanceLoading) return '--'
      const value = finiteNumber(this.balance)
      return value === null ? '--' : value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      })
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.balanceRequest += 1
        this.balanceLoading = false
        if (!value) return
        const raw = this.robot && this.robot.raw || {}
        this.amount = this.mode === 'modify' && raw.space !== undefined ? String(raw.space) : ''
        this.balance = null
        this.balanceLoaded = false
        this.loadBalance()
      }
    }
  },
  beforeDestroy() {
    this.balanceRequest += 1
  },
  methods: {
    close() {
      if (!this.submitting) this.$emit('update:visible', false)
    },
    async loadBalance() {
      if (this.balanceLoading) return
      const requestId = ++this.balanceRequest
      this.balanceLoading = true
      this.balanceLoaded = false
      try {
        const result = await loadStrategyBalance(1, 'USDT', {
          type: 1,
          instType: '',
          futureType: ''
        })
        if (requestId !== this.balanceRequest || !this.visible) return
        if (result.error) throw new Error(result.error)
        if (finiteNumber(result.balance) === null) throw new Error(this.$t('可用余额读取失败'))
        this.balance = result.balance
        this.balanceLoaded = true
      } catch (error) {
        if (requestId !== this.balanceRequest || !this.visible) return
        this.balance = null
        this.$message.error({ message: error.message || this.$t('可用余额读取失败'), customClass: 'orbit-feedback' })
      } finally {
        if (requestId === this.balanceRequest) this.balanceLoading = false
      }
    },
    submit() {
      if (this.submitting || this.balanceLoading) return
      const issue = orbitAmountIssue(this.amount, {
        mode: this.mode,
        balance: this.balance,
        balanceLoaded: this.balanceLoaded
      })
      if (issue) {
        this.$message.warning({ message: this.$tk(issue), customClass: 'orbit-feedback' })
        if (issue === ORBIT_I18N_KEYS.balanceRequired) this.loadBalance()
        return
      }
      this.$emit('submit', {
        productKey: 'orbit',
        amount: finiteNumber(this.amount),
        robotType: 1,
        leverage: 10,
        stopLoss: 0,
        lossTransferRate: 70,
        state: 1,
        riskLevel: 'balanced',
        balance: this.balance,
        balanceLoaded: this.balanceLoaded
      })
    }
  }
}
</script>

<style scoped>
.orbit-create {
  position: fixed;
  z-index: 13250;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 17, 38, .46);
}

.orbit-create__panel {
  width: 540px;
  max-width: 94vw;
  max-height: 92vh;
  overflow-y: auto;
  padding: 28px;
  color: #172033;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20, 16, 45, .24);
}

.orbit-create__handle { display: none; }

.orbit-create-enter-active,
.orbit-create-leave-active { transition: opacity .24s ease; }
.orbit-create-enter-active .orbit-create__panel,
.orbit-create-leave-active .orbit-create__panel { transition: transform .24s ease; }
.orbit-create-enter,
.orbit-create-leave-to { opacity: 0; }
.orbit-create-enter .orbit-create__panel,
.orbit-create-leave-to .orbit-create__panel { transform: translateY(12px); }
.orbit-create__error { display: block; margin-top: 7px; color: #ef4444; font-size: 12px; }

.orbit-create__header,
.orbit-create__label-row,
.orbit-create__readonly,
.orbit-create__balance,
.orbit-create__footer {
  display: flex;
  align-items: center;
}

.orbit-create__header { justify-content: space-between; gap: 16px; }
.orbit-create__header h2 { margin: 0; font-size: 20px; font-weight: 800; }
.orbit-create__header p { margin: 5px 0 0; color: #8c96aa; font-size: 13px; }
.orbit-create__header button,
.orbit-create__label-row button {
  color: #8c96aa;
  border: 0;
  background: transparent;
}

.orbit-create__summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  padding: 14px;
  border: 1px solid #dfe7f5;
  border-radius: 10px;
  background: #f7f9fd;
}
.orbit-create__summary > div { min-width: 0; flex: 1; }
.orbit-create__summary strong,
.orbit-create__summary small { display: block; }
.orbit-create__summary small { margin-top: 3px; color: #8c96aa; font-size: 12px; }
.orbit-create__summary em {
  padding: 4px 9px;
  color: #0b7a47;
  border-radius: 999px;
  background: #e8f6ee;
  font-size: 12px;
  font-style: normal;
}
.orbit-create__icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  place-items: center;
  color: #fff;
  border-radius: 11px;
  background: linear-gradient(135deg, #34d9b8, #0b9ba8);
}

.orbit-create__field { margin-top: 17px; }
.orbit-create__field > label,
.orbit-create__label-row label { color: #5d6679; font-size: 13px; font-weight: 600; }
.orbit-create__label-row { justify-content: space-between; }
.orbit-create__label-row button { color: #1f6bff; font-size: 12px; }
.orbit-create__readonly,
.orbit-create__input {
  min-height: 44px;
  margin-top: 8px;
  padding: 0 14px;
  border: 1px solid #dbe3f1;
  border-radius: 9px;
  background: #f8f9fc;
}
.orbit-create__readonly { gap: 9px; }
.orbit-create__readonly--between { justify-content: space-between; }
.orbit-create__dot { width: 7px; height: 7px; border-radius: 50%; background: #12b76a; }
.orbit-create__input { display: flex; align-items: center; }
.orbit-create__input input { min-width: 0; flex: 1; border: 0; outline: 0; background: transparent; font: inherit; }
.orbit-create__input input::placeholder { color: #b7c0d1; font-weight: 400; }
.orbit-create__input span { color: #8c96aa; font-size: 12px; }
.orbit-create__balance { justify-content: space-between; margin-top: 8px; color: #8c96aa; font-size: 12px; }
.orbit-create__balance strong { color: #1f6bff; }
.orbit-create__hint { display: block; margin-top: 7px; color: #8c96aa; font-size: 12px; }

.orbit-create__notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 12px 14px;
  color: #087744;
  border-radius: 9px;
  background: #e9f7f0;
  font-size: 12px;
  line-height: 1.55;
}
.orbit-create__notice i { flex: 0 0 auto; line-height: 1; }
.orbit-create__notice span { display: block; }
.orbit-create__footer { gap: 12px; margin-top: 22px; }
.orbit-create__footer button {
  height: 46px;
  flex: 1;
  border: 1px solid #d8e0ed;
  border-radius: 9px;
  background: #fff;
  font-weight: 700;
}
.orbit-create__footer button:last-child { color: #fff; border-color: #1f6bff; background: #1f6bff; }
.orbit-create__footer button:disabled { cursor: not-allowed; opacity: .6; }

@media (max-width: 760px) {
  .orbit-create { align-items: flex-end; padding: 0; }
  .orbit-create__panel {
    width: 100%;
    max-width: none;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    padding: 28px 18px calc(50px + env(safe-area-inset-bottom, 0px));
    border-radius: 20px 20px 0 0;
    box-shadow: none;
  }
  .orbit-create__handle {
    display: block;
    width: 36px;
    height: 4px;
    margin: -7px auto 14px;
    border-radius: 999px;
    background: #dbe1eb;
  }
  .orbit-create__header h2 { font-size: 18px; }
  .orbit-create__footer { position: sticky; bottom: 0; padding-top: 12px; background: #fff; }
  .orbit-create-enter .orbit-create__panel,
  .orbit-create-leave-to .orbit-create__panel { transform: translateY(100%); }
}
</style>

<style>
/* Orbit-only feedback must remain above its own sheet and backdrop. */
.el-message.orbit-feedback { z-index: 14010 !important; }
@media (max-width: 760px) {
  .el-message.orbit-feedback { min-width: 0; max-width: calc(100vw - 28px); width: max-content; }
}
</style>
